import { ImportedAccountGroup } from "@common/schemas/domains/accounting/importedAccountGroup.schema";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { AccountMappingSpreadsheetRow, AccountRow, GroupRow } from "./types";
import { compareBySortOrderThenName } from "./compareBySortOrderThenName";

interface BuildClassificationRowsResult {
    rows: AccountMappingSpreadsheetRow[];
    accountCount: number;
}

function uniformValue<T>(values: Array<T | null>): T | null {
    const [first] = values;
    if (first == null) {
        return null;
    }
    return values.every((value) => value === first) ? first : null;
}

export function buildClassificationRows(
    classification: AccountClass,
    groups: ImportedAccountGroup[],
    accountRows: AccountRow[],
): BuildClassificationRowsResult {
    const childrenByParentId = new Map<string | null, ImportedAccountGroup[]>();
    for (const group of groups) {
        if (group.classification !== classification) {
            continue;
        }
        const list = childrenByParentId.get(group.parentGroupId) ?? [];
        list.push(group);
        childrenByParentId.set(group.parentGroupId, list);
    }
    for (const [key, list] of childrenByParentId.entries()) {
        childrenByParentId.set(key, [...list].sort(compareBySortOrderThenName));
    }

    const accountsByGroupId = new Map<string | null, AccountRow[]>();
    for (const row of accountRows) {
        if (row.accountClass !== classification) {
            continue;
        }
        const list = accountsByGroupId.get(row.importedAccountGroupId) ?? [];
        list.push(row);
        accountsByGroupId.set(row.importedAccountGroupId, list);
    }
    for (const [key, list] of accountsByGroupId.entries()) {
        accountsByGroupId.set(key, [...list].sort(compareBySortOrderThenName));
    }

    const result: AccountMappingSpreadsheetRow[] = [];
    let accountCount = 0;
    const visited = new Set<string>();

    const walk = (group: ImportedAccountGroup, depth: number): AccountRow[] => {
        if (visited.has(group.id)) {
            return [];
        }
        visited.add(group.id);

        const groupRow: GroupRow = {
            _id: group.id,
            _depth: depth,
            _isGroup: true,
            name: group.name,
            accountClass: classification,
            providerType: null,
            accountType: null,
            accountSubtype: null,
        };
        result.push(groupRow);

        const subtreeAccounts: AccountRow[] = [];
        const children = childrenByParentId.get(group.id) ?? [];
        for (const child of children) {
            subtreeAccounts.push(...walk(child, depth + 1));
        }

        const accountsForGroup = accountsByGroupId.get(group.id) ?? [];
        for (const account of accountsForGroup) {
            result.push({ ...account, _depth: depth + 1 });
            subtreeAccounts.push(account);
            accountCount++;
        }

        groupRow.accountType = uniformValue(
            subtreeAccounts.map((account) => account.accountType),
        );
        groupRow.accountSubtype = uniformValue(
            subtreeAccounts.map((account) => account.accountSubtype),
        );
        groupRow.providerType = uniformValue(
            subtreeAccounts.map((account) => account.providerType),
        );
        return subtreeAccounts;
    };

    const rootGroups = childrenByParentId.get(null) ?? [];
    for (const root of rootGroups) {
        walk(root, 0);
    }

    const ungroupedAccounts = accountsByGroupId.get(null) ?? [];
    for (const account of ungroupedAccounts) {
        result.push({ ...account, _depth: 0 });
        accountCount++;
    }

    return { rows: result, accountCount };
}
